import { useTheme } from "@emotion/react";
import { Button, Stack, styled, Typography } from "@mui/material";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import CustomImageContainer from "../../CustomImageContainer";
import CustomModal from "../../modal";
import { CustomStackFullWidth } from "../../../styled-components/CustomStyles.style";
import { Box, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

const appleicon = `${process.env.PUBLIC_URL}/static/footer/apple.svg`;
const playstoreicon = `${process.env.PUBLIC_URL}/static/footer/playstore.svg`;

export const CustomButton = styled(Button)(({ theme, graybackground }) => ({
	// width: "115px",
	height: "50px",
	padding: "8px 15px",
	borderRadius: "5px",
	cursor: "pointer",
	backgroundColor:
		graybackground === "true"
			? theme.palette.footer.appDownloadButtonBgGray
			: theme.palette.footer.appDownloadButtonBg,
	"&:hover": {
		backgroundColor: theme.palette.footer.appDownloadButtonBgHover,
	},
	[theme.breakpoints.down("md")]: {
		maxWidth: "140px",
		height: "50px",
	},
}));
const AppLinks = (props) => {
	const { graybackground, landingPageData } = props;
	const theme = useTheme();
	const [modalOpen, setModalOpen] = useState(false);
	const [modalContent, setModalContent] = useState({ title: "", message: "", url: "" });

	let language_direction;
	if (typeof window !== "undefined") {
		language_direction = window.localStorage.getItem("direction");
	}
	
	const handleDownload = (url, platform) => {
		if (platform === "android") {
			if (url && url.includes("apk")) {
				// Show modal with Download Now button for APK files
				setModalContent({
					title: t("Download App"),
					message: t("Ready to download! Click the button below to get the app."),
					url: url
				});
				setModalOpen(true);
			} else if (url) {
				// Redirect to Play Store or other URL
				setModalContent({
					title: t("Coming Soon"),
					message: t("Coming soon on Play Store! You can download from the link below."),
					url: url
				});
				setModalOpen(true);
			} else {
				setModalContent({
					title: t("Coming Soon"),
					message: t("Coming soon on Play Store!"),
					url: ""
				});
				setModalOpen(true);
			}
		} else if (platform === "ios") {
			// Check if URL is blank or generic Apple App Store URL
			if (!url || url === "https://www.apple.com/app-store/") {
				// Show coming soon modal with no download option
				setModalContent({
					title: t("Coming Soon"),
					message: t("Coming soon on App Store!"),
					url: ""
				});
				setModalOpen(true);
			} else {
				// Show coming soon modal with download option for other URLs
				setModalContent({
					title: t("Coming Soon"),
					message: t("Coming soon on App Store! You can access it from the link below."),
					url: url
				});
				setModalOpen(true);
			}
		}
	};
	
	const handleModalClose = () => {
		setModalOpen(false);
		setModalContent({ title: "", message: "", url: "" });
	};
	
	const { t } = useTranslation();
	const googlePlay = () => (
		<CustomButton
			onClick={() =>
				handleDownload(landingPageData?.download_user_app_links?.playstore_url, "android")
			}
			variant="contained"
			graybackground={graybackground ? "true" : "false"}
		>
			<Stack
				direction="row"
				alignItems="center"
				justifyContent="space-between"
				spacing={0.5}
			>
				<CustomImageContainer
					src={playstoreicon}
					alt="GooglePlay"
					objectFit="cover"
					height="26px"
					width="26px"
				/>
				<Stack alignItems="flex-start" justifyContent="center">
					<Typography
						sx={{
							fontSize: { xs: "8px", sm: "10px", md:"12px" },
							color: theme.palette.whiteContainer.main,
						}}
					>
						{t("GET IT ON")}
					</Typography>
					<Typography
						sx={{
							fontWeight: 700,
							fontSize: { xs: "10px", sm: "13px",md:"16px" },
						}}
						color={theme.palette.whiteContainer.main}
					>
						Google Play
					</Typography>
				</Stack>
			</Stack>
		</CustomButton>
	);
	const appleStore = () => (
		<CustomButton
			onClick={() =>
				handleDownload(landingPageData?.download_user_app_links?.apple_store_url, "ios")
			}
			variant="contained"
			graybackground={graybackground ? "true" : "false"}
		>
			<Stack
				direction="row"
				alignItems="center"
				justifyContent="space-between"
				spacing={0.5}
			>
				<CustomImageContainer
					src={appleicon}
					alt="App Store"
					objectFit="cover"
					height="33px"
					width="33px"
				/>
				<Stack alignItems="flex-start" justifyContent="center">
					<Typography
						sx={{
							fontSize: { xs: "8px", sm: "10px", md:"12px" },
							color: theme.palette.whiteContainer.main,
						}}
					>
						{t("Download ON")}
					</Typography>
					<Typography
						sx={{
							fontWeight: 700,
							fontSize: { xs: "10px", sm: "13px",md:"16px" },
						}}
						color={theme.palette.whiteContainer.main}
					>
						{t("App Store")}
					</Typography>
				</Stack>
			</Stack>
		</CustomButton>
	);
	return (
		<>
			<Stack
				direction="row"
				spacing={2}
				// sx={{ mt: 4 }}
				gap={language_direction === "rtl" && "10px"}
				justifyContent="center"
			>
				{googlePlay()}
				{appleStore()}
			</Stack>
			
			<CustomModal openModal={modalOpen} handleClose={handleModalClose}>
				<Box
					sx={{
						position: "relative",
						padding: "24px",
						backgroundColor: (theme) => theme.palette.background.paper,
						color: (theme) => theme.palette.text.primary,
						borderRadius: "8px",
						minWidth: "300px",
						maxWidth: "400px",
						textAlign: "center"
					}}
				>
					<IconButton
						onClick={handleModalClose}
						sx={{
							position: "absolute",
							top: "8px",
							right: "8px"
						}}
					>
						<CloseIcon />
					</IconButton>
					
					<CustomStackFullWidth spacing={2}>
						<Typography variant="h6" fontWeight="bold">
							{modalContent.title}
						</Typography>
						<Typography variant="body1" color="text.secondary">
							{modalContent.message}
						</Typography>
						{modalContent.url && (
							<Button
								variant="contained"
								color="primary"
								onClick={() => {
									window.open(modalContent.url);
									handleModalClose();
								}}
								sx={{ mt: 1 }}
							>
								{t("Download Now")}
							</Button>
						)}
					</CustomStackFullWidth>
				</Box>
			</CustomModal>
		</>
	);
};

AppLinks.propTypes = {};

export default AppLinks;
